import angular from "angular";

import { Arrays, InputEnumOption, Objects, Translations } from "@salaxy/core";
import { InputController } from "./InputController";
import { EnumerationsLogic } from "@salaxy/core";

import { createPopper } from "@popperjs/core";

/**
 * Controller behind form controls that select an typeahead type.
 */
export class InputTypeaheadController extends InputController {

  public static $inject = ["$timeout", "$element", "$scope"];

  /** Options of the select control as a key-value object. */
  protected _options: any;

  /**
   * Array or comma separated string to filter the option values to just the given ones.
   * Also sets the order to this order, so works for ordering a given set of values.
   */
  public filter: string[];

  /** If set to true, does not cache enums. */
  public disableCache: boolean;

  /**
   * Label format for the option. The default format is to show only the text of the option.
   * Options are:
   * "text" or empty: only the text of the option is displayed.
   * "text-value": in addition to the text, the value is also displayed in the label.
   * "value": only the value is displayed.
   * "title": Title (description of enumeration) if available. Defaults to text.
   */
  public labelFormat:  "text" | "value" | "text-value" | "title";

  private enumCache: InputEnumOption[] = [];

  /** List of typeahead options. */
  public filteredItems: InputEnumOption[] = [];

  /** Upon selecting a value from typeahead list, this function is triggered. */
  public onSelectFunction?: (args?: any) => void;

  /** Format values to a requried format. */
  public inputFormatter?: (value: string | number) => string;

  /** Function to get options for the typeahead list. */
  public getOptions?: any;

  /** Typeahead works as a "forwarding" element (searching for a value adds a row), not as an input field to which write values. */
  public clearOnSelect: boolean = false;

  /** Some typeahead inputs allow inserting values (such as tags) to database. */
  public allowCustomValues: boolean = false;

  /** Enums type typeahead, the enum options are fetched with EnumerationsLogic. */
  public enum: any;

  /** Object containing all data of typeahead selection. */
  public objectData: any;

  /** The typeahead result list. */
  private resultList: HTMLElement;

  /** The popper.js menu instance. */
  private popperInstance: any;

  /** True = return object as well alongside the typical value. */
  public returnObject: boolean = false;

  /** True = The value is saved as a whole object, false = as a primitive (string/number/etc. from the item key "value") */
  public valueAsObject: boolean = false;

  /** Event listener listens for key presses (Up, Down, Tab and Enter). This flags it's status to prevent multiple listeners due to possible race conditions. */
  private isKeyListenerActive = false;

  /** The <input> field which acts as the search field. */
  private inputField: HTMLInputElement;

  /** User-written text search string. */
  public searchText: string = "";

  /** Flag to make sure the list initializes only once. */
  private isInitialized: boolean = false;

  constructor(private $timeout: angular.ITimeoutService, private $element: JQLite, private $scope: any) {
    super();
  }

  /** Sets the default values in init. */
  public $onInit() {
    if (this.enum) {
      this.filteredItems = this.getEnumerations();
    }
    super.$onInit();
    this.$timeout(() => {
      if (this.value && typeof this.value === "object") {
        this.value = (this.value as { value?: string; text?: string }).value ?? "";
      }
    });
  }

  /** Readable value of the chosen option. */
  public get displayText() {
    if (!this.value) return "";

    let rawValue: string;

    if (typeof this.value === "object") {
      const valObj = this.value as { text?: string; value?: string };
      rawValue = valObj.text || valObj.value || "";
    } else {
      rawValue = this.value;
    }

    return this.getEnumerationLabel(rawValue);
  }

  /** Delete dom stuff - click event, result list and popper instance - upon destroying of the component (for example changing the tab). */
  public $onDestroy() {
    if (this.resultList?.remove) {
      this.resultList.remove();
    }
    this.popperInstance?.destroy();
  }

  /** Creates the result list popper element. */
  private initResultList() {
    if (this.isInitialized) return;

    this.resultList = this.$element[0].querySelector(".typeahead-result-list");
    this.inputField = this.$element[0].querySelector("input.typeahead-input");
    this.isInitialized = true;
    const body = angular.element(document.body);

    this.popperInstance = createPopper(
      this.$element[0].querySelector(".typeahead-wrapper"),
      this.resultList,
      {
        placement: "bottom-start",
        modifiers: [
          {
            name: "flip",
            enabled: false,
          },
        ],
      }
    );

    body.append(this.resultList);

    /** On-click event listener for hiding the results list. */
    const onClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!this.$element[0].contains(target) && this.resultList && !this.resultList.contains(target)) {
        if (window.getComputedStyle(this.resultList).display !== "none") {
          this.closeResultsList();
        }
      }
    };

    document.addEventListener("click", onClick);

    this.$scope.$on("$destroy", () => {
      this.popperInstance.destroy();
      this.popperInstance = null;
      document.removeEventListener("click", onClick);
    });
  }

  /** Show results list. */
  private showResultsList() {
    // Forced removal. Some modals have typeaheads whose ng-hides aren't removed on input click.
    this.resultList.classList.remove("d-none", "ng-hide", "no-results");

    // Fixes the search result list height on tighter screens.
    const refEl = this.popperInstance.state.elements.reference as HTMLElement;
    const refRect = refEl.getBoundingClientRect();
    const available = Math.round(window.innerHeight - (refRect.bottom + 10));
    const clamped = Math.max(160, Math.min(480, available));

    this.resultList.style.maxHeight = `${clamped}px`;

    this.popperInstance.update();

    if (!this.isKeyListenerActive) {
      window.addEventListener("keydown", this.onKeyDown);
      this.isKeyListenerActive = true;
    }

    this.$timeout(() => {
      const firstItem = this.resultList.children[0];
      firstItem.classList.add("keyboard-selected");
    });
  }

  /**
   * Hide results list.
   */
  private closeResultsList() {
    // If closing call came via event listener, timeout is needed so that AngularJS can detect it.
    this.$timeout(() => {
      this.filteredItems = [];
      this.resultList.classList.add("d-none");
      this.resultList.classList.remove("no-results");
      window.removeEventListener("keydown", this.onKeyDown);
      this.isKeyListenerActive = false;
      this.searchText = "";
    });
  }

  /** Options list */
  public get options(): any[] {
    return this._options;
  }
  public set options(value: any[]) {
    // Convert array of strings/numbers into objects.
    if (Array.isArray(value) && value.length && typeof value[0] !== "object") {
      value = value.map(v => ({ value: v, text: v }));
    }

    this._options = value;
  }

  /** Select an item from the typeahead list. */
  public selectItem(item: any) {
    // "No selection" selected from the list.
    if (!item.value) {
      this.clearValue(true);
      this.closeResultsList();
      return;
    }

    if (this.onSelectFunction) {
      this.onSelectFunction(item.value);
    }

    this.value = this.valueAsObject ? item : item.value;

    if (this.returnObject) {
      // Data can be fetched asynchronously, so a timeout is needed.
      this.$timeout(() => {
        this.objectData = item.object;
      });
    }
    // Update model and close the results list.
    this.closeResultsList();
    super.onChange();

    // Special case: adding new calc rows via the typeahead requires clearing of the element.
    if (this.clearOnSelect) {
      this.$timeout(() => {
        this.clearValue(false);
        this.closeResultsList();
      });
    }
  }

  /**
   * Gets the label for an enumeration.
   * @param value Type of the enum.
   */
  public getEnumerationLabel(value: string): string {
    if (this.inputFormatter) return this.inputFormatter(value);

    const option = this.getEnumerations().find((x) => x.value === value);
    return option ? this.formatLabel(option) : value;
  }

  /** Formats the label for display and typeahed searches. */
  public formatLabel(option: InputEnumOption) {
    switch(this.labelFormat) {
      case "value":
        return option.value;
      case "text-value":
        {
          let text = option.text ? Translations.get(option.text) : option.text;
          text = text ? text : option.value;
          return `${text}${option.value ? " (" + option.value +")" : ""}`;
        }
      case "title":
        return Translations.get(option.title) ?? Translations.get(option.text);
      case "text":
      default:
        return Translations.get(option.text);
    }
  }

  /**
   * Returns items for current enumeration.
   */
  protected getEnumerations(): InputEnumOption[] {
    if (this.enumCache.length > 0 && !this.disableCache) {
      return this.enumCache;
    }
    let allValues: InputEnumOption[] = [];

    if (this.options) {
      if (Array.isArray(this.options)) {
        allValues = this.options.map((item: any) => {
          if (item == null) {
            return { value: null, text: "[null]" };
          }
          if (Objects.has(item, "value") || Objects.has(item, "text")) {
            return {
              value: item.value,
              text: item.text,
              title: (item.title && item.title !== item.value) ? item.title : null,
              ui: item.ui ? item.ui : null
            };
          }
          return { value: item, text: item };
        });
      } else {
        allValues = Object.keys(this.options).map((key) => ({ value: key, text: this.options[key], title: null }));
      }
    } else if (this.enum) {
      const enums = EnumerationsLogic.getEnumMetadata(this.enum);
      if (enums) {
        allValues = enums.values
          .map((e) => ({
            value: e.name,
            text: e.label,
            title: e.descr && e.descr !== e.name ? e.descr : null
          }));
      } else {
        allValues = [{
          value: "",
          text: `ERROR: ${this.enum} not found.`,
          title: null,
        }];
      }
    }

    const filterArr = Arrays.assureArray(this.filter);
    if (filterArr.length > 0) {
      allValues = filterArr.map((x) => allValues.find((y) => y.value === x)).filter((x) => x != null);
    }
    this.modifyOptions(allValues, this.enumCache);
    return this.enumCache;
  }

  private modifyOptions(
    source: ({
      /** Enum value. */
      value: any,
      /** Label for the value. */
      text: string,
      /** Description for the value. */
      title?: string,
    })[],
    target: ({
      /** Enum value. */
      value: any,
      /** Label for the value. */
      text: string,
      /** Description for the value. */
      title?: string,
    })[]) {
    // check if source values differ from target
    const ok = angular.equals(source, target);

    if (ok) {
      return;
    }
    // remove values
    target.splice(0, target.length);

    // add values
    target.push(...source);
  }

  /** Filter items based on the input query */
  public filterEnumItems(showAll: boolean) {
    if (showAll) {
      this.filteredItems = this.getEnumerations();
    } else {
      if (!this.value) {
        this.filteredItems = this.getEnumerations();
      } else {
        const allItems = this.getEnumerations();

        this.filteredItems = allItems.filter(item => {
          const text = Translations.get(item.text) ?? "";
          const title = item.title ? Translations.get(item.title) ?? "" : "";

          return text.toLowerCase().includes(this.value.toLowerCase()) || title.toLowerCase().includes(this.value.toLowerCase());
        });
      }
    }
  }

  /** Reacts to the changing input value. */
  public async onInputChange(): Promise<void> {
    if (this.getOptions) {
      this.filteredItems = await Promise.resolve(this.getOptions(this.searchText));
    } else if (this.options) {
      this.filteredItems = this.options.filter(option => {
        return option.text?.toLowerCase().includes(this.searchText.toLowerCase()) || option.title?.toLowerCase().includes(this.searchText.toLowerCase()) || option.value?.toLowerCase().includes(this.searchText.toLowerCase());
      });
    } else {
      this.filterEnumItems(false);
    }

    if (this.filteredItems.length > 0) {
      this.showResultsList();
    } else {
      if (this.allowCustomValues && this.searchText) {
        return;
      }
      // Wait for the search results to update before proceeding.
      this.$timeout(() => {
        this.filteredItems.push({ value: null, text: Translations.get("SALAXY.UI_Terms.noResults") });
        this.resultList.classList.add("no-results");
      });
    }
  }

  /**
   * Clears the selected value in the input field.
   */
  public clearValue(updateModel = true): void {
    this.$timeout(() => {
      this.value = "";
      if (updateModel) {
        super.onChange();
      }

      if (this.returnObject) {
        this.objectData = {};
      }
    });
  }

  /** Show all typeahead results (that are currently at hand).*/
  public async openResultsList() {
    this.initResultList();

    if (this.options) {
      this.filteredItems = this.options;
    } else if (this.getOptions) {
      this.filteredItems = await Promise.resolve(this.getOptions());
    } else if (this.enum) {
      this.filteredItems = this.getEnumerations();
    }

    if (this.resultList && this.inputField) {
      this.showResultsList();
      this.inputField.focus({ preventScroll: true });
    }
  }

  /**
   * Method to register key down events (arrows Up/Down + Enter are supported).
   * @param event Captured keyboard event.
   */
  private onKeyDown = (event: KeyboardEvent) => {
    if (this.resultList && ["ArrowUp", "ArrowDown", "Enter", "Tab"].includes(event.key)) {
      // Tab hides the results list, as the user keyboard-navigates out of the input field.
      if (event.key == "Tab") {
        this.closeResultsList();
        return;
      }
      let currentSelected: Element;
      let currentIndex: number;

      for (let i = 0; i < this.resultList.children.length; i++) {
        const el = this.resultList.children[i];
        if (el.classList.contains("keyboard-selected")) {
          currentSelected = el;
          currentIndex = i;
          break;
        }
      }

      // Select the currently selected item.
      if (event.key == "Enter" && currentSelected && getComputedStyle(this.resultList).display !== "none") {
        (currentSelected as HTMLElement).click();
        this.inputField.blur();
      // Shuffle through the list with arrow keys.
      } else {
        if (event.key == "ArrowUp" && currentIndex > 0) {
          currentIndex--;
        } else if (event.key == "ArrowDown" && currentIndex < this.resultList.children.length-1) {
          currentIndex++;
        }

        if (currentSelected) {
          currentSelected.classList.remove("keyboard-selected");
        }
        this.resultList.children[currentIndex].classList.add("keyboard-selected");
        this.resultList.children[currentIndex].scrollIntoView({ block: "nearest" });
      }
    }
  };
}